import { apiClient } from './client'
import type { ApiResponse } from './client'

export interface LeadInteractionDto {
  InteractionType: string // Human readable (e.g., Webinar Registration, Webinar Attended, Resource Download)
  OccurredAt: string // ISO string
  // Generic classification (formerly WebType)
  Type: string
  // Generic source / display name (formerly WebName)
  Name: string
  Notes?: string | null
  Weight: number // Raw weight contribution
  // The Web/Webinar this interaction belongs to — enables web-scoped timeline filtering
  WebId?: string | null
  // BigMarker deletion support (webinar interactions only)
  DeletedInSourceAt?: string | null // ISO datetime when detected as deleted in source
  ConferenceId?: string | null // BigMarker conference ID
  RegistrationId?: string | null // BigMarker registration ID
  // Email interactions only:
  EmailSendId?: string | null // deep-link to the EmailSend preview (ORB-283)
  EmailType?: string | null // "Marketing" | "Transactional" — drives the timeline badge (ORB-381)
}

export interface LeadSignalTimelineDto {
  // Identity
  LeadId: string
  ETag: string
  CompanyId?: string | null
  Email?: string | null
  FullName?: string | null
  FirstName?: string | null
  LastName?: string | null
  JobTitle?: string | null
  Industry?: string | null
  CompanySize?: string | null
  Company?: string | null
  CompanyIndustry?: string | null
  CompanyStaffCountRange?: string | null
  CompanyLinkedInUrl?: string | null
  CompanyLogoUrl?: string | null
  CompanyWebsite?: string | null
  Domain?: string | null
  Phone?: string | null
  PhoneEnrichmentAvailableDate?: string | null // ISO8601
  GlobalContactPhone?: string | null
  Country?: string | null

  // Enrichment status
  EnrichmentStatus?: string | null
  EnrichedAt?: string | null // ISO8601
  EnrichmentAvailableDate?: string | null // ISO8601
  EnrichmentAttemptCount: number

  // Enrichment visuals / links
  LinkedinProfileUrl?: string | null
  ProfilePicUrl?: string | null

  // Editable fields exposed for UI editing
  CompanyLinkedInId?: string | null
  ContactId?: string | null

  // Derived Web breadth
  WebTypes: string[]

  // Interactions timeline (chronological ascending)
  Interactions: LeadInteractionDto[]

  // Per-lead campaign touchpoints
  CampaignTouchpoints?: CampaignTouchpointDto[]

  // Web source attributions (for web-level lead grids)
  WebSourceAttributions: WebSourceAttributionDto[]

  // Scoring
  SignalScore: number
  SignalLevel: string // Low | Medium | High (server computed)

  // Recency metrics
  FirstInteractionAt?: string | null
  LastInteractionAt?: string | null
  InteractionCount: number

  // Audience targeting inference flags
  IsExplicitAudienceTarget?: boolean // true if matched explicitly uploaded audience/company
  IsDomainInferredAudienceTarget?: boolean // true if domain inference matched audience domains (not explicit)
  IsAudienceTarget?: boolean // convenience (Explicit OR DomainInferred)

  // ORB-619: this person's email domain is on a non-prospect list — ours, the customer's own staff,
  // or an event's guest co-host. A SEPARATE axis from IsAudienceTarget, not a third state of it.
  // Derived per request, so removing a domain clears it on the next read.
  IsNonProspect?: boolean

  // Webinar attendance (only populated if campaign has Online Event web)
  WebinarAttended?: boolean | null

  // Delete permission
  CanDelete: boolean
}

export interface CompanySignalDto {
  /**
   * GROUPING KEY, not an addressable id — and not the same kind of value at every scope. An email
   * domain at workspace scope; a document id falling back to a domain at campaign and web scope.
   * Nothing on the row says which, so never navigate on it. Use `CompanyProfileId`.
   */
  CompanyId?: string | null
  /**
   * The id to open a company profile at, or null when there is no company to open (ORB-623).
   *
   * Not a promise that we hold a company record: it falls back to the identified-but-unstored id,
   * which the profile renders with limited detail. Null means nobody in the group was matched to a
   * company at all — typically a personal email domain.
   */
  CompanyProfileId?: string | null
  CompanyName: string
  CompanyWebsite?: string | null
  CompanyLinkedInUrl?: string | null
  CompanyLogoUrl?: string | null
  Industry?: string | null
  StaffCountRange?: string | null
  SignalScore: number
  LastConversionAt?: string | null
  /** Prospects only, matching the Contacts view (ORB-619). */
  LeadCount: number
  /** True when every lead behind this company is a non-prospect. Hidden by default. */
  IsNonProspect?: boolean
  /** True when ANY lead behind this company was targeted — drives the Companies audience filter. */
  IsAudienceTarget?: boolean
}

export interface CampaignLeadAnalyticsResponseDto {
  CampaignId: string
  Leads: LeadSignalTimelineDto[]
  Companies: CompanySignalDto[]
  /** Prospects only — non-prospects are excluded server-side (ORB-619). */
  LeadCount: number
  /** How many of Leads were held back from LeadCount. */
  NonProspectCount: number
  CompanyCount: number
  GeneratedAtUtc: string
}

export interface CampaignTouchpointDto {
  CampaignId: string
  CampaignName: string
  FirstInteractionAt?: string | null
  InteractionCount: number
  SignalScore: number
}

export interface WebSourceAttributionDto {
  WebId: string
  Source: string
  HasAcceptedOffer: boolean
}

// DTO for updating web source attribution
export interface WebSourceAttributionUpdateDto {
  ETag: string
  Source: string
  HasAcceptedOffer: boolean
}

export interface WorkspaceLeadAnalyticsResponseDto {
  Leads: LeadSignalTimelineDto[]
  Companies: CompanySignalDto[]
  /** Prospects only — non-prospects are excluded server-side (ORB-619). */
  LeadCount: number
  /** How many of Leads were held back from LeadCount. */
  NonProspectCount: number
  CompanyCount: number
  GeneratedAtUtc: string
}

// DTO for updating lead fields
export interface LeadUpdateDto {
  ETag: string
  Email?: string | null
  FirstName?: string | null
  LastName?: string | null
  JobTitle?: string | null
  Phone?: string | null
  CompanyLinkedInId?: string | null
  // Override fields when not using LinkedIn company
  CompanyName?: string | null
  Industry?: string | null
  CompanySize?: string | null
  CompanyWebsite?: string | null
}

// DTO returned from phone enrichment endpoint
export interface PhoneEnrichmentResultDto {
  /// Phone number on Lead (may be manually edited)
  Phone?: string | null
  /// Enriched phone number from global Contact
  GlobalContactPhone?: string | null
  /// Date when phone enrichment will be available again (null if available now)
  PhoneEnrichmentAvailableDate?: string | null // ISO8601
}

// DTO returned from web source attribution update
export interface LeadViewDto {
  Id: string
  ETag: string
}

// DTO for lead grid view (used in search results)
export interface LeadGridDto {
  Id: string
  ETag: string
  Email?: string | null
  AlternateEmails?: string[] | null
  FirstName?: string | null
  LastName?: string | null
  JobTitle?: string | null
  Industry?: string | null
  CompanySize?: string | null
  Phone?: string | null
  CompanyLinkedInId?: string | null
  CompanyName?: string | null
  CompanyWebsite?: string | null
  Country?: string | null
  EnrichmentStatus?: string | null
  EnrichedAt?: string | null
  EnrichmentAttemptCount?: number
  ProfilePicUrl?: string | null
}

// DTO for merge search results (simpler, includes match confidence)
export interface LeadMergeSearchResultDto {
  Id: string
  FirstName: string | null
  LastName: string | null
  Email: string
  JobTitle: string | null
  CompanyName: string | null
  ProfilePicUrl: string | null
  /** Match confidence percentage (0-100) based on similarity to search criteria */
  MatchConfidencePercent: number
}

// Request DTO for merging leads
export interface MergeLeadsRequestDto {
  /** The Lead ID that will be deleted. Its email will be added to the survivor's AlternateEmails list. */
  DuplicateLeadId: string
  /** The Lead ID that will survive and receive all merged data. */
  SurvivingLeadId: string
}

// Response DTO from merge operation
export interface MergeLeadsResponseDto {
  MergedContact: LeadGridDto
  Message: string
  UpdatedEntitiesCount: number
}

// Search parameters for finding leads to merge with
export interface SearchForMergeParams {
  firstName?: string
  lastName?: string
  email?: string
  companyName?: string
  linkedInUrn?: string
  excludeId?: string  // Exclude current lead from results
}

export const leadsApi = {
  getCampaignLeads: (campaignId: string, workspaceId: string, signal?: AbortSignal): Promise<ApiResponse<CampaignLeadAnalyticsResponseDto>> => {
    const url = `/api/Workspaces/${encodeURIComponent(workspaceId)}/Campaigns/${encodeURIComponent(campaignId)}/Leads`
    return apiClient.get(url, { signal })
  },
  getWebLeads: (campaignId: string, workspaceId: string, webId: string, signal?: AbortSignal): Promise<ApiResponse<CampaignLeadAnalyticsResponseDto>> => {
    const url = `/api/Workspaces/${encodeURIComponent(workspaceId)}/Campaigns/${encodeURIComponent(campaignId)}/Webs/${encodeURIComponent(webId)}/Leads`
    return apiClient.get(url, { signal })
  },
  getWorkspaceLeads: (workspaceId: string, signal?: AbortSignal): Promise<ApiResponse<WorkspaceLeadAnalyticsResponseDto>> => {
    const url = `/api/Workspaces/${encodeURIComponent(workspaceId)}/Leads`
    return apiClient.get(url, { signal })
  },
  update: (workspaceId: string, campaignId: string, leadId: string, payload: LeadUpdateDto, signal?: AbortSignal): Promise<ApiResponse<LeadUpdateDto>> => {
    return apiClient.put(`/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/Leads/${leadId}/Update`, payload, { signal })
  },
  enrichPhone: (workspaceId: string, campaignId: string, leadId: string, signal?: AbortSignal): Promise<ApiResponse<PhoneEnrichmentResultDto>> => {
    return apiClient.post(`/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/Leads/${leadId}/EnrichPhone`, {}, { signal })
  },
  syncPhone: (workspaceId: string, campaignId: string, leadId: string, signal?: AbortSignal): Promise<ApiResponse<PhoneEnrichmentResultDto>> => {
    return apiClient.post(`/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/Leads/${leadId}/SyncPhone`, {}, { signal })
  },
  reEnrich: (workspaceId: string, campaignId: string, leadId: string, signal?: AbortSignal): Promise<ApiResponse<void>> => {
    return apiClient.post(`/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/Leads/${leadId}/re-enrich`, {}, { signal })
  },
  updateWebSourceAttribution: (workspaceId: string, campaignId: string, leadId: string, webId: string, payload: WebSourceAttributionUpdateDto, signal?: AbortSignal): Promise<ApiResponse<LeadViewDto>> => {
    return apiClient.patch(`/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/Leads/${leadId}/WebSourceAttributions/${webId}`, payload, { signal })
  },
  delete: (workspaceId: string, campaignId: string, leadId: string, signal?: AbortSignal): Promise<ApiResponse<void>> => {
    return apiClient.delete(`/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/Leads/${leadId}`, { signal })
  },
  /**
   * Search for leads to merge with
   */
  searchForMerge: (
    workspaceId: string,
    campaignId: string,
    params: SearchForMergeParams,
    signal?: AbortSignal
  ): Promise<ApiResponse<LeadMergeSearchResultDto[]>> => {
    const queryParams = new URLSearchParams()
    if (params.firstName) queryParams.append('firstName', params.firstName)
    if (params.lastName) queryParams.append('lastName', params.lastName)
    if (params.email) queryParams.append('email', params.email)
    if (params.companyName) queryParams.append('companyName', params.companyName)
    if (params.linkedInUrn) queryParams.append('linkedInUrn', params.linkedInUrn)
    if (params.excludeId) queryParams.append('excludeId', params.excludeId)

    return apiClient.get(
      `/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/Leads/search-for-merge?${queryParams.toString()}`,
      { signal }
    )
  },
  /**
   * Merge two leads
   */
  merge: (
    workspaceId: string,
    campaignId: string,
    request: MergeLeadsRequestDto,
    signal?: AbortSignal
  ): Promise<ApiResponse<MergeLeadsResponseDto>> => {
    return apiClient.post(
      `/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/Leads/merge`,
      request,
      { signal }
    )
  },
  /**
   * Clean up a webinar registration that was deleted in BigMarker
   */
  cleanupRegistration: (
    workspaceId: string,
    campaignId: string,
    conferenceId: string,
    registrationId: string,
    signal?: AbortSignal
  ): Promise<ApiResponse<void>> => {
    return apiClient.post(
      `/api/Workspaces/${encodeURIComponent(workspaceId)}/Campaigns/${encodeURIComponent(campaignId)}/Conferences/${encodeURIComponent(conferenceId)}/Registrations/${encodeURIComponent(registrationId)}/Cleanup`,
      {},
      { signal }
    )
  }
}
